import { useState } from 'react'
import { Column, Task } from './types'

type Company = {
  id: number
  name: string
}

type TaskModalProps = {
  columns: Column[]
  companies: Company[]
  initialColumnId: string
  isAdmin: boolean
  creator: string
  task?: Task
  onClose: () => void
  onSave: (task: Omit<Task, 'id'> & { id?: string }) => void
}

export const TaskModal = ({
  columns,
  companies,
  initialColumnId,
  isAdmin,
  creator,
  task,
  onClose,
  onSave
}: TaskModalProps) => {
  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [dueDate, setDueDate] = useState(task?.dueDate ?? '')
  const [columnId, setColumnId] = useState(task?.columnId ?? initialColumnId)
  const [isSecret, setIsSecret] = useState(task?.isSecret ?? false)
  const [companyId, setCompanyId] = useState<number | null>(
    task?.companyId ?? null
  )
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (title.trim() === '') {
      setError('タイトルは必須です。')
      return
    }
    onSave({
      id: task?.id,
      title: title.trim(),
      description,
      dueDate,
      creator: task?.creator ?? creator,
      columnId,
      companyId,
      isSecret: isAdmin ? isSecret : false,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-5 text-lg font-bold text-gray-800">
          {task ? 'タスクを編集' : 'タスクを追加'}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              カラム
            </label>
            <select
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            >
              {columns.map((col) => (
                <option key={col.id} value={col.id}>
                  {col.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              注力顧客（会社）
            </label>
            <select
              value={companyId ?? ''}
              onChange={(e) =>
                setCompanyId(e.target.value ? Number(e.target.value) : null)
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            >
              <option value="">選択しない</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="タスクのタイトル"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              説明
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="タスクの説明（任意）"
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              期日
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#01695C]"
            />
          </div>

          {isAdmin && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSecret}
                  onChange={(e) => setIsSecret(e.target.checked)}
                  className="h-4 w-4 accent-purple-600"
                />
                <div>
                  <span className="text-sm font-medium text-purple-800">
                    🔒 シークレットモード
                  </span>
                  <p className="mt-0.5 text-xs text-purple-600">
                    有効にすると、このタスクは管理者にのみ表示されます。
                  </p>
                </div>
              </label>
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-lg bg-[#01695C] px-4 py-2 text-sm text-white hover:opacity-90"
          >
            {task ? '更新' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}
